import React from "react";
import { Link } from "react-router-dom";
import { getRequest, postRequest } from "sharedUtils/httpUtils";
import MUIDataTable from "mui-datatables";
import TextField from '@material-ui/core/TextField';
import AffiliateMerchantData from './AffiliateMerchantData';
import AffiliateMerchantRawData from './AffiliateMerchantRawData';
import { convertToNumericMonthAndDayAndYearAndTime } from "sharedUtils/timeUtils";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { CheckIconBlack } from "../../icons";
import PenIcon from "components/icons/PenIcon";

const { DateTime } = require("luxon");

class AffiliateMerchantDetails extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();

        this.state = {
            startDate: DateTime.utc().minus({ months: 1 }).startOf('day'),
            endDate: DateTime.utc().endOf('day'),
            minDate: "1753-01-01",
            maxDate: "9999-12-31",
            dateError: false,
            tab: 0,
            affiliateProgramManager: null,
            apmId: this.props.apmId ?? null,
            brands: [],
            brandCount: 0,
            integratedCount: 0,
            deepLinkOnlyCount: 0,
            alarms: [],
            subsLoading: false,
            BrandsTable: {
                title: "Affiliate Brands",
                options: {                    
                    selectableRows: "multiple",
                    rowsPerPage: 100,
                    sortOrder: {
                        name: 'merchantName',
                        direction: 'asc'
                    },
                },
                getTheme: () => createTheme({
                    typography: {
                        fontFamily: "'Urbanist', 'sf_pro_displayregular', sans-serif",
                    },
                    overrides: {
                        MUIDataTableBodyRow: {
                            root: {
                                '&:nth-child(odd)': {
                                    backgroundColor: '#F9F9F9'
                                },
                                cursor: 'pointer'
                            }
                        },
                    }
                })
            },
        };
    }

    componentDidMount = () => {
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        this.fetchAffiliateProgramManager();
        this.getBrands();
    };


    handleStartDateChange = (event) => {
        const startDateRaw = event.target.value;
        if (startDateRaw.length === 0) return;

        const startDate = DateTime.fromISO(startDateRaw).toUTC().startOf('day');
        if (startDate.isValid) {
            this.setState({ startDate });
        } else {
            console.error("Invalid start date entered:", startDateRaw);
        }
    };

    handleEndDateChange = (event) => {
        const endDateRaw = event.target.value;
        if (endDateRaw.length === 0) return;

        const endDate = DateTime.fromISO(endDateRaw).toUTC().endOf('day');
        if (endDate.isValid) {
            this.setState({ endDate });
        } else {
            console.error("Invalid end date entered:", endDateRaw);
        }
    };

    handleWeekToDate = () => {
        const startDate = DateTime.utc().minus({ weeks: 1 }).startOf('day');
        this.setState({ startDate, endDate: DateTime.utc().endOf('day') });
    };

    handleMonthToDate = () => {
        const startDate = DateTime.utc().minus({ months: 1 }).startOf('day');
        this.setState({ startDate, endDate: DateTime.utc().endOf('day') });
    };

    handleYearToDate = () => {
        const startDate = DateTime.utc().minus({ years: 1 }).startOf('day');
        this.setState({ startDate, endDate: DateTime.utc().endOf('day') });
    };

    fetchAffiliateProgramManager = async (id) => {
        let url = `api/AffiliateProgramManager/GetAffiliateProgramManager?id=${this.state.apmId}`;
        let affiliateProgramManager = await getRequest(url);
        if (affiliateProgramManager) {
            this.setState({ affiliateProgramManager })
        }
    }

    getBrands = async () => {
        let url = `/api/Merchant/GetCommissionEstimateFullCommission?affiliateId=${this.state.apmId}`;

        this.setState({ subsLoading: true });

        try {
            //const brandsResults = await getRequest(url);
            //const brands = brandsResults.filter(brand => brand.affiliateProgramManagerId === parseInt(this.state.apmId));
            const brands = await getRequest(url);

            const brandCount = brands.length;
            const integratedCount = brands.filter(brand => !!brand.integrated).length;
            const deepLinkOnlyCount = brands.filter(brand => !!brand.deepLinkOnly).length;

            this.setState({
                brands,
                subsLoading: false,
                brandCount,
                integratedCount,
                deepLinkOnlyCount
            });
        } catch (error) {
            console.error("Error fetching brands:", error);
            this.setState({ brands: [], subsLoading: false });
        }
    };

    onTabChange = (tab) => {
        this.setState({ tab });
    }

    render() {
        let smallSizes = ["sm", "md"];
        let { deviceSize } = this.props;

        const columns = [
            {
                name: "merchantName",
                label: "Name",
                options: {
                    filter: true,
                    display: true,
                    setCellProps: () => ({
                        style: { width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
                    })
                },
            },
            {
                name: "commission",
                label: "Commission",
                options: {
                    filter: true,
                    display: true,
                    customBodyRender: (value) => {
                        let commissionPercentage = value * 100;
                        let roundedCommissionPercentage = commissionPercentage % 1 === 0 ? commissionPercentage : commissionPercentage.toFixed(2);

                        return <p style={{ textAlign: "center", margin: 0 }}  >{roundedCommissionPercentage}%</p>
                    },
                    setCellProps: () => ({
                        style: { width: "1%", minWidth: "100px" } // Minimum width for smaller columns
                    })
                },
            },
            {
                name: "integrated",
                label: "Integrated",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <p style={{ textAlign: "center", margin: 0 }}>{!!(value) ? <CheckIconBlack /> : ""}</p>
                    },
                    setCellProps: () => ({
                        style: { width: "1%", minWidth: "100px" } // Minimum width for smaller columns
                    })
                },
            },
            {
                name: "deepLinkOnly",
                label: "Deep Link Only",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return <p style={{ textAlign: "center", margin: 0 }}>{!!(value) ? <CheckIconBlack /> : ""}</p>
                    },
                    setCellProps: () => ({
                        style: { width: "1%", minWidth: "100px" } // Minimum width for smaller columns
                    })
                },
            },
            {
                name: "deleted",
                label: "Active",
                options: {
                    filter: true,
                    display: true,
                    customBodyRender: (value) => {
                        return <p style={{ textAlign: "center", margin: 0 }}>{!(value) ? <CheckIconBlack /> : ""}</p>
                    },
                    setCellProps: () => ({
                        style: { width: "1%", minWidth: "100px" } // Minimum width for smaller columns
                    })
                },
            },
            {
                name: "id",
                label: "Edit",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value, tableMeta) => {
                        const merchantAffiliateProgramManagerId = tableMeta.rowData[
                            columns.findIndex(col => col.name === "merchantAffiliateProgramManagerId")
                        ];

                        return (
                            <Link to={`/Admin/Brand/${value}?m=${merchantAffiliateProgramManagerId}&a=${this.state.apmId}`}>
                                <PenIcon />
                            </Link>
                        );
                    },
                    setCellProps: () => ({
                        style: { width: "1%", minWidth: "100px" } // Minimum width for smaller columns
                    })
                },
            },

            {
                name: "merchantAffiliateProgramManagerId",
                label: "Merchant Affiliate Program Manager ID",
                options: {
                    filter: true,
                    sort: true,
                    display: false,
                },
            },
        ];

        return (
            <div style={{ display: this.props.display ? "" : "none" }}>

                <section className="gray-bg mb-0 pt-0" style={{ display: this.state.tab === 2 ? "" : "none" }}>
                    {
                        !["sm"].includes(this.props.deviceSize) ?
                            <></>
                            :
                            <div className="d-flex w-100 justify-content-end pr-2" style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                <div onClick={this.handleWeekToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', border: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '4px 0 0 4px' }}>1w</div>
                                <div onClick={this.handleMonthToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', borderTop: '1px solid lightgray', borderBottom: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>1m</div>
                                <div onClick={this.handleYearToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', border: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '0 4px 4px 0' }}>1y</div>
                            </div>
                    }
                    <p className="ml-5 pt-3 mb-0" style={{ color: "red", display: this.state.dateError ? "" : "none" }}>Dates must be valid dates and Start Date must be before the End Date </p>

                    <div className="pt-0 pb-0 px-5">

                        <div className={`mx-0 d-flex justify-content-between pb-4 ${["sm"].includes(this.props.deviceSize) ? "ml-2 mr-2" : "pt-0"} mx-auto`}>

                            <div className={`d-flex ${["sm"].includes(this.props.deviceSize) ? "w-100 pt-3 mx-2" : ""} justify-content-${["sm"].includes(this.props.deviceSize) ? "between" : "start"}`} style={{ gap: ["sm"].includes(this.props.deviceSize) ? "" : "30px", fontFamily: "'Urbanist',sans-serif" }}>

                                <div className="d-flex flex-column">
                                    <label className="mb-0">Start Date:</label>
                                    <TextField
                                        id="start-date"
                                        type="date"
                                        value={this.state.startDate.toISODate()}
                                        onChange={this.handleStartDateChange}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            style: { fontFamily: "'Urbanist', sans-serif" },
                                        }} />
                                </div>
                                <div className="d-flex flex-column">
                                    <label className="mb-0">End Date:</label>
                                    <TextField
                                        id="end-date"
                                        type="date"
                                        value={this.state.endDate.toISODate()}
                                        onChange={this.handleEndDateChange}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            style: { fontFamily: "'Urbanist', sans-serif" },
                                        }}
                                    />
                                </div>
                            </div>
                            {
                                ["sm"].includes(this.props.deviceSize) ?
                                    <></>
                                    :
                                    <div className="d-flex" style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                        <div onClick={this.handleWeekToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', border: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '4px 0 0 4px' }}>1w</div>
                                        <div onClick={this.handleMonthToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', borderTop: '1px solid lightgray', borderBottom: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>1m</div>
                                        <div onClick={this.handleYearToDate} style={{ cursor: "pointer", width: '35px', height: '35px', backgroundColor: 'white', border: '1px solid lightgray', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '0 4px 4px 0' }}>1y</div>
                                    </div>
                            }

                        </div>
                    </div>

                </section>

                <section>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col">
                                <ul className={`nav nav-tabs justify-content-end`}>
                                    <li className={`nav-item ${smallSizes.includes(deviceSize) ? "my-products-tab-ul-small" : ""} `} onClick={() => this.onTabChange(0)}>
                                        <span className={`nav-link text-center ${smallSizes.includes(deviceSize) && this.state.tab === 0 ? "my-products-tab-small-active" : smallSizes.includes(deviceSize) ? "my-products-tab-small" : this.state.tab === 0 ? "my-products-tab-active" : "my-products-tab"}`}>{this.state.affiliateProgramManager?.name + " Brands"}</span>
                                    </li>
                                    <li className={`nav-item ${smallSizes.includes(deviceSize) ? "my-products-tab-ul-small" : ""} `} onClick={() => this.onTabChange(1)}>
                                        <span className={`nav-link text-center ${smallSizes.includes(deviceSize) && this.state.tab === 1 ? "my-products-tab-small-active" : smallSizes.includes(deviceSize) ? "my-products-tab-small" : this.state.tab === 1 ? "my-products-tab-active" : "my-products-tab"}`}>{this.state.affiliateProgramManager?.name + " Merchant Data"}</span>
                                    </li>
                                    <li className={`nav-item ${smallSizes.includes(deviceSize) ? "my-products-tab-ul-small" : ""} `} onClick={() => this.onTabChange(2)}>
                                        <span className={`nav-link text-center ${smallSizes.includes(deviceSize) && this.state.tab === 2 ? "my-products-tab-small-active" : smallSizes.includes(deviceSize) ? "my-products-tab-small" : this.state.tab === 2 ? "my-products-tab-active" : "my-products-tab"}`}>{this.state.affiliateProgramManager?.name + " Merchant Raw Data"}</span>
                                    </li>
                                </ul>
                                {
                                    this.state.tab === 0 ?
                                        <ThemeProvider theme={this.state.BrandsTable.getTheme()}>
                                            <MUIDataTable
                                                title={
                                                    <div style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                                        <p style={{ fontSize: '22px', marginTop: "20px" }}>{this.state.affiliateProgramManager?.name + " Brands" ?? "No affiliate selected"}</p>
                                                        <p style={{ fontSize: '14px', marginBottom: "0" }}>Brands: {this.state.brandCount}</p>
                                                        <p style={{ fontSize: '14px', marginBottom: "0" }}>Integrated: {this.state.integratedCount}</p>
                                                        <p style={{ fontSize: '14px', marginBottom: "0" }}>Deep Link Only: {this.state.deepLinkOnlyCount}</p>
                                                    </div>
                                                }
                                                data={this.state.brands}
                                                columns={columns}
                                                options={{
                                                    ...this.state.BrandsTable.options,
                                                    textLabels: {
                                                        body: { noMatch: this.state.subsLoading ? "LOADING..." : "No data available" },
                                                    },
                                                    selectableRows: "none",
                                                }}
                                                ref={this.tableRef}
                                            />
                                        </ThemeProvider>
                                        :
                                        this.state.tab === 1 ?
                                            <AffiliateMerchantData affiliateProgramManager={this.state.affiliateProgramManager} loggedInUser={this.props.loggedInUser} />
                                            :
                                            <AffiliateMerchantRawData affiliateProgramManager={this.state.affiliateProgramManager} loggedInUser={this.props.loggedInUser} startDate={this.state.startDate} endDate={this.state.endDate} />

                                }
                                
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default AffiliateMerchantDetails;
